import React from "react";
import { useTranslation } from "react-i18next";
import PhoneInput from "../../../../../components/PhoneInput";
import Input from "../../../../../components/Input";
import Switch from "../../../../../components/Switch";
import Dropdown from "../../../../../components/Dropdown";
import TextArea from "../../../../../components/TextArea";
import { VALIDATION_FIELD_MAPPING } from "../../../../../utils/appConstant";
import { Validation } from "../../../../../interfaces/common";
import { AddEdit as AddEditInterface } from "../../../../../interfaces/customer.interface";
import Address from "./Address";

interface CustomerDetailsProps {
  view?: boolean;
  formData: AddEditInterface;
  validation: Validation;
  dropdownSearchData: any;
  salutations: any[];
  allSalutations: any[];
  billingCurrency: any[];
  allBillingCurrency: any[];
  countries: any[];
  states: any[];
  allCountries: any[];
  allStates: any[];
  allCities: any[];
  cities: any[];
  onKeyDown: (e: any, name?: string) => void;
  onInputBlur: (e: any, name: string) => void;
  onInputChange: (e: any, name: string) => void;
  onDropdownSearchChange: (e: any, name: string) => void;
}

const CustomerDetails: React.FC<CustomerDetailsProps> = ({
  view, formData, validation, dropdownSearchData, salutations, allSalutations, onDropdownSearchChange, countries, states, allCountries, allStates, allCities, cities, onKeyDown, onInputBlur, onInputChange, allBillingCurrency, billingCurrency
}) => {
  const { t } = useTranslation()

  return (
    <form className="card-body" autoComplete="off">
      <div className="row g-6">
        <h6 className="mb-0">
          1. {t("customers.addEdit.pageTexts.form.sections.primaryDetails")}
        </h6>

        <div className="col-md-6">
          <div className="form-floating form-floating-outline">
            <Input
              type="text"
              required
              id={VALIDATION_FIELD_MAPPING.customerName}
              name={VALIDATION_FIELD_MAPPING.customerName}
              error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.customerName)}
              errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.customerName)?.message ?? ''}
              label={t("customers.addEdit.pageTexts.form.customer.label")}
              placeholder={t("customers.addEdit.pageTexts.form.customer.placeholder")}
              value={formData.customerName}
              onKeyDown={onKeyDown}
              onBlur={e => onInputBlur(e, VALIDATION_FIELD_MAPPING.customerName)}
              onChange={e => onInputChange(e, VALIDATION_FIELD_MAPPING.customerName)}
              disabled={!!view}
            />
          </div>
        </div>
        <div className="col-md-6">
          <div className="form-floating form-floating-outline">
            <Input
              type="text"
              required
              id={VALIDATION_FIELD_MAPPING.displayName}
              name={VALIDATION_FIELD_MAPPING.displayName}
              error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.displayName)}
              errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.displayName)?.message ?? ''}
              label={t("customers.addEdit.pageTexts.form.displayName.label")}
              placeholder={t("customers.addEdit.pageTexts.form.displayName.placeholder")}
              value={formData.displayName}
              onKeyDown={onKeyDown}
              onBlur={e => onInputBlur(e, VALIDATION_FIELD_MAPPING.displayName)}
              onChange={e => onInputChange(e, VALIDATION_FIELD_MAPPING.displayName)}
              disabled={!!view}
            />
          </div>
        </div>
      </div>
      <hr className="my-6 mx-n4" />
      <div className="row g-6">
        <h6 className="mb-0">
          2. {t("customers.addEdit.pageTexts.form.sections.contactDetails")}
        </h6>

        <div className="col-md-2">
          <div className="form-floating form-floating-outline">
            <Dropdown
              values={formData.primarySalutation ? [formData.primarySalutation] : []}
              options={(salutations || []).map((salutation: any) => ({
                label: salutation.name,
                value: salutation.id,
              }))}
              allOptions={(allSalutations || []).map((salutation: any) => ({
                label: salutation.name,
                value: salutation.id,
              }))}
              searchText={dropdownSearchData.primarySalutation}
              onSearch={onDropdownSearchChange}
              id={VALIDATION_FIELD_MAPPING.primarySalutation}
              name={VALIDATION_FIELD_MAPPING.primarySalutation}
              placeholder={t("customers.addEdit.pageTexts.form.primarySalutation.placeholder")}
              label={t("customers.addEdit.pageTexts.form.primarySalutation.label")}
              onChange={onInputChange}
              onBlur={onInputBlur}
              disabled={!!view}
            />
          </div>
        </div>
        <div className="col-md-5">
          <div className="form-floating form-floating-outline">
            <Input
              type="text"
              required
              id={VALIDATION_FIELD_MAPPING.primaryFirstName}
              name={VALIDATION_FIELD_MAPPING.primaryFirstName}
              error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.primaryFirstName)}
              errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.primaryFirstName)?.message ?? ''}
              label={t("customers.addEdit.pageTexts.form.primaryFirstName.label")}
              placeholder={t("customers.addEdit.pageTexts.form.primaryFirstName.placeholder")}
              value={formData.primaryFirstName}
              onKeyDown={onKeyDown}
              onBlur={e => onInputBlur(e, VALIDATION_FIELD_MAPPING.primaryFirstName)}
              onChange={e => onInputChange(e, VALIDATION_FIELD_MAPPING.primaryFirstName)}
              disabled={!!view}
            />
          </div>
        </div>
        <div className="col-md-5">
          <div className="form-floating form-floating-outline">
            <Input
              type="text"
              required
              id={VALIDATION_FIELD_MAPPING.primaryLastName}
              name={VALIDATION_FIELD_MAPPING.primaryLastName}
              error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.primaryLastName)}
              errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.primaryLastName)?.message ?? ''}
              label={t("customers.addEdit.pageTexts.form.primaryLastName.label")}
              placeholder={t("customers.addEdit.pageTexts.form.primaryLastName.placeholder")}
              value={formData.primaryLastName}
              onKeyDown={onKeyDown}
              onBlur={e => onInputBlur(e, VALIDATION_FIELD_MAPPING.primaryLastName)}
              onChange={e => onInputChange(e, VALIDATION_FIELD_MAPPING.primaryLastName)}
              disabled={!!view}
            />
          </div>
        </div>
        <div className="col-md-6">
          <div className="form-floating form-floating-outline">
            <Input
              type="email"
              required
              id={VALIDATION_FIELD_MAPPING.primaryEmail}
              name={VALIDATION_FIELD_MAPPING.primaryEmail}
              disabled={!!view}
              error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.primaryEmail)}
              errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.primaryEmail)?.message ?? ''}
              label={t("customers.addEdit.pageTexts.form.primaryEmail.label")}
              placeholder={t("customers.addEdit.pageTexts.form.primaryEmail.placeholder")}
              value={formData.primaryEmail}
              onKeyDown={onKeyDown}
              onBlur={e => onInputBlur(e, VALIDATION_FIELD_MAPPING.primaryEmail)}
              onChange={e => onInputChange(e, VALIDATION_FIELD_MAPPING.primaryEmail)}
            />
          </div>
        </div>
        <div className="col-md-6">
          <div className="form-floating form-floating-outline">
            <Input
              type="email"
              id={VALIDATION_FIELD_MAPPING.secondaryEmail}
              name={VALIDATION_FIELD_MAPPING.secondaryEmail}
              error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.secondaryEmail)}
              errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.secondaryEmail)?.message ?? ''}
              label={t("customers.addEdit.pageTexts.form.secondaryEmail.label")}
              placeholder={t("customers.addEdit.pageTexts.form.secondaryEmail.placeholder")}
              value={formData.secondaryEmail}
              onKeyDown={onKeyDown}
              onBlur={e => onInputBlur(e, VALIDATION_FIELD_MAPPING.secondaryEmail)}
              onChange={e => onInputChange(e, VALIDATION_FIELD_MAPPING.secondaryEmail)}
              disabled={!!view}
            />
          </div>
        </div>
        <div className="col-md-6">
          <div className="form-floating form-floating-outline">
            <PhoneInput
              type="text"
              required
              defaultCountry="CA"
              id={VALIDATION_FIELD_MAPPING.primaryPhone}
              name={VALIDATION_FIELD_MAPPING.primaryPhone}
              error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.primaryPhone)}
              errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.primaryPhone)?.message ?? ''}
              label={t("customers.addEdit.pageTexts.form.phoneNumber.label")}
              placeholder={t("customers.addEdit.pageTexts.form.phoneNumber.placeholder")}
              value={(formData.countryCode || "") + (formData.primaryPhone || "")}
              onKeyDown={onKeyDown}
              onBlur={e => onInputBlur(e, VALIDATION_FIELD_MAPPING.primaryPhone)}
              onChange={e => onInputChange(e, VALIDATION_FIELD_MAPPING.primaryPhone)}
              disabled={!!view}
            />
          </div>
        </div>
        <div className="col-md-6">
          <div className="form-floating form-floating-outline">
            <PhoneInput
              type="text"
              defaultCountry="CA"
              id={VALIDATION_FIELD_MAPPING.emergencyPhone}
              name={VALIDATION_FIELD_MAPPING.emergencyPhone}
              error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.emergencyPhone)}
              errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.emergencyPhone)?.message ?? ''}
              label={t("customers.addEdit.pageTexts.form.emergencyPhone.label")}
              placeholder={t("customers.addEdit.pageTexts.form.emergencyPhone.placeholder")}
              value={(formData.emergencyCountryCode || "") + (formData.emergencyPhone || "")}
              onKeyDown={onKeyDown}
              onBlur={e => onInputBlur(e, VALIDATION_FIELD_MAPPING.emergencyPhone)}
              onChange={e => onInputChange(e, VALIDATION_FIELD_MAPPING.emergencyPhone)}
              disabled={!!view}
            />
          </div>
        </div>
        {/* <div className="col-md-6">
          <div className="form-floating form-floating-outline">
            <Input
              type="url"
              id={VALIDATION_FIELD_MAPPING.website}
              name={VALIDATION_FIELD_MAPPING.website}
              error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.website)}
              errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.website)?.message ?? ''}
              label={t("customers.addEdit.pageTexts.form.website.label")}
              placeholder={t("customers.addEdit.pageTexts.form.website.placeholder")}
              value={formData.website}
              onKeyDown={onKeyDown}
              onBlur={e => onInputBlur(e, VALIDATION_FIELD_MAPPING.website)}
              onChange={e => onInputChange(e, VALIDATION_FIELD_MAPPING.website)}
              disabled={!!view}
            />
          </div>
        </div> */}
      </div>
      <hr className="my-6 mx-n4" />
      <div className="row g-6">
        <h6 className="mb-0">
          3. {t("customers.addEdit.pageTexts.form.sections.billingDetails")}
        </h6>

        <div className="col-md-5">
          <div className="form-floating form-floating-outline">
            <Input
              type="text"
              id={VALIDATION_FIELD_MAPPING.companyName}
              name={VALIDATION_FIELD_MAPPING.companyName}
              error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.companyName)}
              errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.companyName)?.message ?? ''}
              label={t("customers.addEdit.pageTexts.form.companyName.label")}
              placeholder={t("customers.addEdit.pageTexts.form.companyName.placeholder")}
              value={formData.companyName}
              onKeyDown={onKeyDown}
              onBlur={e => onInputBlur(e, VALIDATION_FIELD_MAPPING.companyName)}
              onChange={e => onInputChange(e, VALIDATION_FIELD_MAPPING.companyName)}
              disabled={!!view}
            />
          </div>
        </div>
        <div className="col-md-5">
          <div className="form-floating form-floating-outline">
            <Input
              type="text"
              id={VALIDATION_FIELD_MAPPING.hstNo}
              name={VALIDATION_FIELD_MAPPING.hstNo}
              error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.hstNo)}
              errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.hstNo)?.message ?? ''}
              label={t("customers.addEdit.pageTexts.form.hstNo.label")}
              placeholder={t("customers.addEdit.pageTexts.form.hstNo.placeholder")}
              value={formData.hstNo}
              onKeyDown={onKeyDown}
              onBlur={e => onInputBlur(e, VALIDATION_FIELD_MAPPING.hstNo)}
              onChange={e => onInputChange(e, VALIDATION_FIELD_MAPPING.hstNo)}
              disabled={!!view}
            />
          </div>
        </div>
        <div className="col-md-2">
          <div className="form-floating form-floating-outline">
            <Dropdown
              values={formData.billingCurrency ? [formData.billingCurrency] : []}
              options={(billingCurrency || []).map((currency: any) => ({
                label: currency.name,
                value: currency.id,
              }))}
              allOptions={(allBillingCurrency || []).map((currency: any) => ({
                label: currency.name,
                value: currency.id,
              }))}
              id={VALIDATION_FIELD_MAPPING.billingCurrency}
              name={VALIDATION_FIELD_MAPPING.billingCurrency}
              searchText={dropdownSearchData.billingCurrency}
              placeholder={t("customers.addEdit.pageTexts.form.billingCurrency.placeholder")}
              label={t("customers.addEdit.pageTexts.form.billingCurrency.label")}
              required
              error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.billingCurrency)}
              errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.billingCurrency)?.message ?? ''}
              onChange={onInputChange}
              onSearch={onDropdownSearchChange}
              onBlur={onInputBlur}
              disabled={!!view}
            />
          </div>
        </div>
      </div>
      <hr className="my-6 mx-n4" />
      <div className="row g-6">
        <h6 className="mb-0">
          4. {t("customers.addEdit.pageTexts.form.sections.addressDetails")}
        </h6>

        <Address
          countries={countries}
          states={states}
          allCountries={allCountries}
          allStates={allStates}
          allCities={allCities}
          cities={cities}
          formData={formData}
          validation={validation}
          dropdownSearchData={dropdownSearchData}
          onDropdownSearchChange={onDropdownSearchChange}
          onKeyDown={onKeyDown}
          onInputBlur={onInputBlur}
          onInputChange={onInputChange}
          view={view}
        />
      </div>
      <hr className="my-6 mx-n4" />
      <div className="row g-6">
        <h6 className="mb-0">5. {t("customers.addEdit.pageTexts.form.sections.otherInformation")}</h6>
        <div className="col-md-9">
          <div className="form-floating form-floating-outline">
            <TextArea
              id={VALIDATION_FIELD_MAPPING.notes}
              name={VALIDATION_FIELD_MAPPING.notes}
              error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.notes)}
              errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.notes)?.message ?? ''}
              label={t("customers.addEdit.pageTexts.form.notes.label")}
              placeholder={t("customers.addEdit.pageTexts.form.notes.placeholder")}
              value={formData.notes}
              rows={4}
              onKeyDown={onKeyDown}
              onBlur={e => onInputBlur(e, VALIDATION_FIELD_MAPPING.notes)}
              onChange={e => onInputChange(e, VALIDATION_FIELD_MAPPING.notes)}
              className="form-control-lg h-100"
              disabled={!!view}
            />
          </div>
        </div>
        <div className="col-md-3 d-flex align-items-start">
          <div className="form-floating form-floating-outline">
            <Switch
              label={formData.status ? t("customers.addEdit.pageTexts.form.status.activeLabel") : t("customers.addEdit.pageTexts.form.status.inActiveLabel")}
              checked={formData.status}
              name={VALIDATION_FIELD_MAPPING.status}
              disabled={!!view}
              onChange={e => onInputChange(e, VALIDATION_FIELD_MAPPING.status)}
            />
          </div>
        </div>
      </div>
    </form>
  )
};

export default CustomerDetails;